import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Upload,
  ArrowLeft,
  FileText,
  CheckCircle,
  Image as ImageIcon,
  Plus,
  X,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { ModuleItem } from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect from "../components/CustomSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import "../styles/QuestionBankStyles.css";

const QuestionUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [moduleId, setModuleId] = useState<string>("");
  const [moduleName, setModuleName] = useState<string>("");

  // Add Module Modal state
  const [showAddModuleModal, setShowAddModuleModal] = useState<boolean>(false);
  const [newModuleName, setNewModuleName] = useState<string>("");
  const [savingModule, setSavingModule] = useState<boolean>(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingTemplate, setDownloadingTemplate] =
    useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  // Post-upload optional image attachment state
  const [targetQuestionId, setTargetQuestionId] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  const fetchModules = async () => {
    try {
      const res = await QuestionBankClient.listModules();
      if (res && res.modules && res.modules.length > 0) {
        const modItems = res.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
        if (!moduleId) {
          setModuleId(modItems[0].id);
          setModuleName(modItems[0].name);
        }
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    setSavingModule(true);
    try {
      const res = await QuestionBankClient.createModule(newModuleName.trim());
      setShowAddModuleModal(false);
      setNewModuleName("");
      await fetchModules();
      if (res && res.module) {
        setModuleId(res.module.module_id);
        setModuleName(res.module.module_name);
      }
      setAlertConfig({
        show: true,
        title: "Module Created",
        message: `Module '${newModuleName}' created and selected successfully.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Error creating module:", err);
      setAlertConfig({
        show: true,
        title: "Module Creation Failed",
        message: err?.response?.data?.detail || "Failed to create module.",
        type: "error",
      });
    } finally {
      setSavingModule(false);
    }
  };

  const handleDownloadTemplate = async (
    fileType: "excel" | "csv" = "excel",
  ) => {
    setDownloadingTemplate(true);
    try {
      const res = await QuestionBankClient.downloadTemplate("MCQ", fileType);
      if (res && res.file_path) {
        window.open(res.file_path, "_blank");
      } else {
        setAlertConfig({
          show: true,
          title: "Template Download",
          message:
            "Could not generate template download URL. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error downloading template:", err);
      setAlertConfig({
        show: true,
        title: "Download Error",
        message: "Failed to download template file from S3.",
        type: "error",
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setAlertConfig({
        show: true,
        title: "File Required",
        message: "Please select an Excel or CSV file to upload.",
        type: "warning",
      });
      return;
    }
    if (!moduleId) {
      setAlertConfig({
        show: true,
        title: "Module Required",
        message: "Please select or add a Module for the questions.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("question_type", "MCQ");
      formData.append("module_id", moduleId);
      formData.append("module_name", moduleName);

      const res = await QuestionBankClient.uploadQuestionsFile(formData);
      setUploadResult(res);

      setAlertConfig({
        show: true,
        title: "Upload Successful",
        message:
          res.message ||
          `Successfully processed ${res.success_count} questions.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Upload Error:", err);
      setAlertConfig({
        show: true,
        title: "Upload Failed",
        message:
          err?.response?.data?.detail || "Failed to upload question file.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadQuestionImage = async (qId: string) => {
    if (!imageFile) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("question_id", qId);
      formData.append("module_id", moduleId);

      await QuestionBankClient.uploadQuestionImage(formData);
      setAlertConfig({
        show: true,
        title: "Image Uploaded",
        message: "Question image attached successfully.",
        type: "success",
      });
      setImageFile(null);
    } catch (err) {
      console.error("Error uploading image asset:", err);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="qb-container">
      <Loader
        isLoading={loading || downloadingTemplate || savingModule}
        message={
          downloadingTemplate
            ? "Generating Template URL..."
            : savingModule
              ? "Creating Module in DB..."
              : "Uploading & Processing File..."
        }
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Header */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            <Upload size={26} /> Bulk Question Upload
          </h1>
          <p className="qb-subtitle">
            Download the MCQ question template, fill in your questions, select
            or create a Module, and upload.
          </p>
        </div>
        <button
          className="qb-btn qb-btn-outline"
          onClick={() => navigate("/dashboard/question-bank")}
        >
          <ArrowLeft size={16} /> Back to Question Bank
        </button>
      </div>

      <div
        className="qb-content-scroll"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}
      >
        {/* Step 1: Select Module & Download Template */}
        <div className="qb-upload-card">
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#07473b",
              marginBottom: "16px",
            }}
          >
            1. Select Module & Download Template
          </h3>

          {/* Module Selector with Add New Module Button */}
          <div className="qb-form-group">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <label className="qb-label" style={{ margin: 0 }}>
                Module <span style={{ color: "#ef4444" }}>*</span>:
              </label>
              {modules.length > 0 && (
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  style={{
                    padding: "4px 10px",
                    fontSize: "12px",
                    color: "#0d6e5b",
                    borderColor: "#0d6e5b",
                  }}
                  onClick={() => setShowAddModuleModal(true)}
                >
                  <Plus size={14} /> Add New Module
                </button>
              )}
            </div>

            {modules.length > 0 ? (
              <CustomSelect
                options={modules.map((m) => ({ value: m.id, label: m.name }))}
                value={moduleId}
                onChange={(val) => {
                  setModuleId(val);
                  const selectedMod = modules.find((m) => m.id === val);
                  if (selectedMod) setModuleName(selectedMod.name);
                }}
                placeholder="Select Module..."
              />
            ) : (
              <div
                style={{
                  padding: "10px 14px",
                  backgroundColor: "#FEF3C7",
                  borderRadius: "8px",
                  color: "#92400E",
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>No modules found</span>
                <button
                  type="button"
                  className="qb-btn qb-btn-primary"
                  style={{ padding: "4px 12px", fontSize: "12px" }}
                  onClick={() => setShowAddModuleModal(true)}
                >
                  Create Module
                </button>
              </div>
            )}
          </div>

          <div style={{ marginTop: "24px", display: "flex", gap: "10px" }}>
            <button
              className="qb-btn qb-btn-secondary"
              style={{ flex: 1 }}
              onClick={() => handleDownloadTemplate("excel")}
              disabled={downloadingTemplate}
            >
              <Download size={16} /> Excel (.xlsx)
            </button>
            <button
              className="qb-btn qb-btn-secondary"
              style={{ flex: 1 }}
              onClick={() => handleDownloadTemplate("csv")}
              disabled={downloadingTemplate}
            >
              <Download size={16} /> CSV (.csv)
            </button>
          </div>
        </div>

        {/* Step 2: Upload Spreadsheet */}
        <div className="qb-upload-card">
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#07473b",
              marginBottom: "16px",
            }}
          >
            2. Upload Question File
          </h3>

          <form onSubmit={handleUploadSubmit}>
            <div
              className="qb-dropzone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <Upload size={48} className="qb-dropzone-icon" />
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  marginBottom: "6px",
                }}
              >
                Drag & Drop Excel/CSV File Here
              </h4>
              <p
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "16px",
                }}
              >
                Supported formats: .xlsx, .xls, .csv
              </p>
              <input
                type="file"
                id="file-upload-input"
                accept=".xlsx, .xls, .csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <label
                htmlFor="file-upload-input"
                className="qb-btn qb-btn-outline"
                style={{ cursor: "pointer" }}
              >
                Browse File
              </label>
            </div>

            {selectedFile && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #dde5e2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                  }}
                >
                  <FileText size={18} color="#0d6e5b" />
                  <strong>{selectedFile.name}</strong> (
                  {(selectedFile.size / 1024).toFixed(1)} KB)
                </div>
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  style={{ padding: "4px 8px", fontSize: "12px" }}
                  onClick={() => setSelectedFile(null)}
                >
                  Remove
                </button>
              </div>
            )}

            <div style={{ marginTop: "24px" }}>
              <button
                type="submit"
                className="qb-btn qb-btn-primary"
                style={{ width: "100%" }}
                disabled={!selectedFile || !moduleId}
              >
                Upload & Process Questions
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Add New Module Modal */}
      {showAddModuleModal && (
        <div
          className="qb-modal-overlay"
          onClick={() => setShowAddModuleModal(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px" }}
          >
            <div className="qb-modal-header">
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#07473b",
                }}
              >
                Create New Module
              </h3>
              <button
                className="qb-modal-close"
                onClick={() => setShowAddModuleModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateModule}>
              <div className="qb-form-group">
                <label className="qb-label">
                  Module Name <span style={{ color: "#ef4444" }}>*</span>:
                </label>
                <input
                  type="text"
                  className="qb-input"
                  placeholder="e.g. System Design, Cloud Computing, Algorithms"
                  value={newModuleName}
                  onChange={(e) => setNewModuleName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  onClick={() => setShowAddModuleModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="qb-btn qb-btn-primary">
                  Create Module
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Summary Result */}
      {uploadResult && (
        <div className="qb-upload-card" style={{ marginTop: "24px" }}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#07473b",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <CheckCircle size={22} color="#15803d" /> Upload Results Summary
          </h3>
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div
              style={{
                padding: "16px",
                background: "#e6f4f1",
                borderRadius: "8px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "12px", color: "#07473b" }}>
                Success Questions
              </span>
              <h2 style={{ color: "#0d6e5b", margin: 0 }}>
                {uploadResult.success_count || 0}
              </h2>
            </div>
            <div
              style={{
                padding: "16px",
                background: "#fef9c3",
                borderRadius: "8px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "12px", color: "#a16207" }}>
                Skipped Duplicates
              </span>
              <h2 style={{ color: "#a16207", margin: 0 }}>
                {uploadResult.skipped_duplicates || 0}
              </h2>
            </div>
            <div
              style={{
                padding: "16px",
                background: "#fee2e2",
                borderRadius: "8px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "12px", color: "#b91c1c" }}>
                Failed Rows
              </span>
              <h2 style={{ color: "#b91c1c", margin: 0 }}>
                {uploadResult.failed_count || 0}
              </h2>
            </div>
          </div>

          {/* Optional Post-Upload Image Attachment Picker */}
          {uploadResult.questions && uploadResult.questions.length > 0 && (
            <div
              style={{
                marginTop: "24px",
                padding: "20px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #dde5e2",
              }}
            >
              <h4
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#07473b",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <ImageIcon size={20} /> Optionally Attach Images to Uploaded
                Questions
              </h4>
              <p
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "16px",
                }}
              >
                Select an uploaded question to attach its corresponding
                diagram/image file to S3.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {uploadResult.questions.map((q: any) => (
                  <div
                    key={q.question_id}
                    style={{
                      padding: "12px",
                      background: "#ffffff",
                      borderRadius: "8px",
                      border: "1px solid #dde5e2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ flex: 1, paddingRight: "12px" }}>
                      <strong style={{ fontSize: "14px" }}>{q.question}</strong>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setImageFile(e.target.files[0]);
                            setTargetQuestionId(q.question_id);
                          }
                        }}
                      />
                      {targetQuestionId === q.question_id && imageFile && (
                        <button
                          className="qb-btn qb-btn-primary"
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                          onClick={() =>
                            handleUploadQuestionImage(q.question_id)
                          }
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? "Uploading..." : "Save Image"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionUploadPage;
